import React from 'react';
import { string, oneOfType, bool } from 'prop-types';
import { injectIntl, intlShape } from '../../util/reactIntl';
import classNames from 'classnames';
import { propTypes } from '../../util/types';
import {
    ensureUser,
    ensureCurrentUser,
    userDisplayNameAsString,
    userAbbreviatedName,
} from '../../util/data';
import { ResponsiveImage, IconBannedUser, NamedLink } from '../../components/';
import css from './Avatar.css';
import { CameraIcon } from '../../icons';
import { renderAnonymIcon, AVATAR_IMAGE_VARIANTS, AVATAR_SIZES } from './helpers';
import config from '../../config';

const { anonymHorseownerName, anonymRiderName, userTypeRider } = config;

export const AvatarComponent = props => {
    const {
        rootClassName,
        className,
        user,
        renderSizes = AVATAR_SIZES,
        disableProfileLink = false,
        previewAllowed,
        avatarType,
        intl,
    } = props;

    const userIsCurrentUser = user && user.type === 'currentUser';
    const avatarUser = userIsCurrentUser ? ensureCurrentUser(user) : ensureUser(user);
    const { profileImage, attributes } = avatarUser;
    const {
        profile: {
            isAnonym,
            publicData: { userType, hasDefaultAvatar },
        },
    } = attributes;

    const isBannedUser = attributes.banned;
    const isDeletedUser = attributes.deleted;

    const hasProfileImage = !isBannedUser && !isDeletedUser && profileImage && profileImage.id;
    const shouldBeAnonymized = Boolean(isAnonym && !hasDefaultAvatar && hasProfileImage);
    const userIsRider = userType === userTypeRider;

    const classes = classNames({
        [css.root]: !rootClassName,
        [rootClassName]: !!rootClassName,
        [className]: !!className,
        [css.hasProfileImage]: hasProfileImage,
        [css.previewAllowed]: previewAllowed,
        [css.disableProfileLink]: disableProfileLink || isAnonym,
        [css.isAnonym]: shouldBeAnonymized,
    });

    const bannedUserDisplayName = intl.formatMessage({
        id: 'Avatar.bannedUserDisplayName',
    });

    const deletedUserDisplayName = intl.formatMessage({
        id: 'Avatar.deletedUserDisplayName',
    });

    const defaultUserDisplayName = isBannedUser
        ? bannedUserDisplayName
        : isDeletedUser
        ? deletedUserDisplayName
        : '';

    const defaultUserAbbreviatedName = '';
    const profileLinkEnabled = !disableProfileLink && !isAnonym;

    const displayName = userDisplayNameAsString(avatarUser, defaultUserDisplayName);
    const abbreviatedName = userAbbreviatedName(avatarUser, defaultUserAbbreviatedName);
    const rootProps = {
        className: classes,
        title: isAnonym ? (userIsRider ? anonymRiderName : anonymHorseownerName) : displayName,
    };
    const linkProps =
        avatarUser.id && userType
            ? { name: 'ProfilePage', params: { id: avatarUser.id.uuid, userType } }
            : { name: 'ProfileBasePage' };

    const AnonymIcon = renderAnonymIcon(avatarType, {
        rootClassName: classNames(css.avatarAnonymIcon, {
            [css[avatarType]]: !!css[avatarType],
        }),
    });

    if (isBannedUser || isDeletedUser) {
        return (
            <div {...rootProps}>
                <IconBannedUser className={css.bannedUserIcon} />
            </div>
        );
    } else if (hasProfileImage && profileLinkEnabled) {
        return (
            <NamedLink {...rootProps} {...linkProps} isNotRouterLink>
                <ResponsiveImage
                    rootClassName={css.avatarImage}
                    alt={displayName}
                    image={avatarUser.profileImage}
                    variants={AVATAR_IMAGE_VARIANTS}
                    sizes={renderSizes}
                />
                <i className={css.avatarLayer}></i>
            </NamedLink>
        );
    } else if (hasProfileImage) {
        return (
            <div {...rootProps}>
                <ResponsiveImage
                    rootClassName={css.avatarImage}
                    alt={displayName}
                    image={avatarUser.profileImage}
                    variants={AVATAR_IMAGE_VARIANTS}
                    sizes={renderSizes}
                />
                <div
                    className={classNames(css.avatarLayer, {
                        [css.previewLayer]: previewAllowed,
                    })}
                >
                    {previewAllowed && (
                        <div className={css.preview}>
                            <CameraIcon />
                            <p>
                                Neues Profilbild <br /> hochladen
                            </p>
                            <span>.GIF, .JPG und .PNG Max. 10 MB</span>
                        </div>
                    )}
                </div>
                {shouldBeAnonymized ? AnonymIcon : null}
            </div>
        );
    } else if (profileLinkEnabled) {
        // Placeholder avatar (initials)
        return (
            <NamedLink {...rootProps} {...linkProps} isNotRouterLink>
                <span className={css.initials}>{abbreviatedName}</span>
            </NamedLink>
        );
    } else {
        // Placeholder avatar (initials)
        return (
            <div {...rootProps}>
                <span className={css.initials}>{abbreviatedName}</span>
            </div>
        );
    }
};

AvatarComponent.propTypes = {
    rootClassName: string,
    className: string,
    user: oneOfType([propTypes.user, propTypes.currentUser]),

    renderSizes: string,
    disableProfileLink: bool,

    // from injectIntl
    intl: intlShape.isRequired,
};

const Avatar = injectIntl(AvatarComponent);

export default Avatar;

export const AvatarMedium = props => (
    <Avatar rootClassName={css.mediumAvatar} renderSizes={AVATAR_SIZES} {...props} />
);
AvatarMedium.displayName = 'AvatarMedium';

export const AvatarLarge = props => (
    <Avatar
        rootClassName={css.largeAvatar}
        renderSizes={AVATAR_SIZES}
        avatarType="large"
        {...props}
    />
);
AvatarLarge.displayName = 'AvatarLarge';

export const AvatarExtraLarge = props => (
    <Avatar
        rootClassName={css.extraLargeAvatar}
        renderSizes={AVATAR_SIZES}
        avatarType="extraLarge"
        {...props}
    />
);
AvatarExtraLarge.displayName = 'AvatarExtraLarge';
